import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import {
    ConfiguratorDatastoreService,
    DatastoreDto,
    GatewayPublicUserService,
    GatewayRoleResourceService,
    RuleDmpDto,
    UserDTO
} from '@tj/tj-dmp-lib-api';
import { MenuItem, MessageService } from 'primeng/api';
import { forkJoin, from, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LANGUAGES } from '../../../../services/lang.service';
import { AccountService } from '../../../../auth/account.service';
import { UserManagementScope } from '../../../../auth/admin-levels';

interface Select {
    name: string;
    code: string;
}

type RightType = 'rule' | 'role';

// Code the API stores in `role` or `rule` when the other one carries the right, and in `partner` for no partner.
const NONE = 'NONE';

@Component({
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.scss'],
    standalone: false
})
export class UserEditComponent implements OnInit {

    readonly languages = LANGUAGES;

    readonly rightTypes: { label: string, value: RightType }[] = [
        { label: this.translate.instant('userEdit.ruleDialog.typeRule'), value: 'rule' },
        { label: this.translate.instant('userEdit.ruleDialog.typeRole'), value: 'role' }
    ];

    // Offered before the partners of a group: no partner, or all of them.
    private readonly partnerChoices: Select[] = [
        { code: NONE, name: this.translate.instant('userEdit.access.partnerNone') },
        { code: 'ALL', name: this.translate.instant('userEdit.access.partnerAll') }
    ];

    breadcrumb: MenuItem[] = [
        { label: this.translate.instant('menu.admin.label') },
        { label: this.translate.instant('menu.admin.users'), routerLink: '/h/admin/user-management' },
        { label: this.translate.instant('userEdit.titleCreate') }];
    actual: UserDTO;
    form: UntypedFormGroup;
    saving = false;
    submitted = false;

    // What the signed-in admin may grant (DMP2-851): the rule dialog only offers that, the gateway checks it.
    scope: UserManagementScope = UserManagementScope.of(null);

    // Every group, for the names shown in the table; the rule dialog only offers `ruleTenants`.
    datastores: DatastoreDto[] = [];
    ruleTenants: DatastoreDto[] = [];
    roles: Select[] = [];
    rules: Select[] = [];

    // The user's access rules, as the API stores them. Edited in the rule dialog, saved with the user.
    userRules: RuleDmpDto[] = [];

    // Rule dialog: it works on `ruleForm`, and only "Ajouter" / "Valider" copies it into `userRules`.
    ruleForm: UntypedFormGroup = this.fb.group({
        tenant: [null, Validators.required],
        partner: [NONE],
        type: ['rule'],
        rule: [null],
        role: [null]
    }, { validators: UserEditComponent.rightChosen });
    ruleDialogVisible = false;
    // Index in `userRules` of the rule being edited; null when the dialog adds a rule.
    editedRuleIndex: number | null = null;
    // Partners of the group chosen in the dialog, and roles for that group and partner.
    ruleFormPartners: Select[] = [];
    ruleFormRoles: Select[] = [];

    constructor(private fb: UntypedFormBuilder,
        private router: Router,
        private messageService: MessageService,
        private route: ActivatedRoute,
        protected datastoreResourceService: ConfiguratorDatastoreService,
        private gatewayPublicUserService: GatewayPublicUserService,
        private gatewayRoleResourceService: GatewayRoleResourceService,
        private accountService: AccountService,
        private readonly translate: TranslateService
    ) {
    }

    // The rule or role matching the chosen type is required; the other one is kept but ignored.
    private static rightChosen(group: AbstractControl): ValidationErrors | null {
        const { type, rule, role } = group.value;
        const right = type === 'role' ? role : rule;
        return right && right !== NONE ? null : { rightRequired: true };
    }

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    get displayName(): string {
        const fullName = [this.actual.firstName, this.actual.lastName].filter(Boolean).join(' ');
        return fullName || this.actual.login;
    }

    ngOnInit(): void {
        const id = this.route.snapshot.paramMap.get('id');

        forkJoin([
            this.datastoreResourceService.getAllDatastoresUsingGET(),
            this.gatewayRoleResourceService.getRoles(),
            this.gatewayRoleResourceService.getRules(),
            from(this.accountService.identity())
        ]).subscribe(([datastores, roles, rules, account]) => {
            this.scope = UserManagementScope.of(account);
            this.datastores = datastores;
            this.ruleTenants = datastores.filter(d => this.scope.coversTenant(d.code));
            this.roles = roles.map(el => ({
                code: el.name,
                name: `${el.name} / ${el.description}`
            }));
            this.rules = rules.map(el => ({
                code: el,
                name: el
            }));
            if (id) {
                this.initOnUpdate(id);
            } else {
                this.initForm();
            }
        });
    }

    initOnUpdate(id: string) {
        this.gatewayPublicUserService.getUser(id).subscribe({
            next: (user: UserDTO) => {
                this.actual = user;
                this.breadcrumb = [...this.breadcrumb.slice(0, 2), { label: this.displayName }];
                this.userRules = user.rules ?? [];
                this.initForm(user);
            },
            error: (e) => {
                console.error('Error loading user:', e);
                this.messageService.add({
                    key: 'common', severity: 'error', summary: this.translate.instant('userEdit.toasts.errorSummary'),
                    detail: this.translate.instant('userEdit.toasts.loadErrorDetail')
                });
            }
        });
    }

    // Without a user, the form of a new user. The email is only a field on creation: it is also the login.
    initForm(user?: UserDTO) {
        this.form = this.fb.group({
            firstname: [user?.firstName ?? '', Validators.required],
            lastname: [user?.lastName ?? '', Validators.required],
            lang: [user ? this.languages.find(lang => lang.code === user.langKey?.toLowerCase())?.code ?? null : this.languages[0].code,
                Validators.required],
            mfaEmail: [user ? user.mfaEmail : true],
            mfaSms: [user ? user.mfaSms : true],
            phone: [user?.mobile ?? ''],
            activated: [user ? user.activated : false],
            admin: [user ? user.admin : false]
        });
        if (!user) {
            this.form.addControl('email', this.fb.control('', [Validators.required, Validators.email]));
        }
    }

    // A field's error is shown under it once the user has changed or left it, not on a pristine form.
    showError(field: string): boolean {
        const control = this.form.get(field);
        return control.invalid && (control.dirty || control.touched);
    }

    // The gateway refuses an account without access rule to an admin who is not super-admin (DMP2-845).
    rulesRequired(): boolean {
        return !this.scope.isSuperAdmin();
    }

    showRulesError(): boolean {
        return this.submitted && this.rulesRequired() && this.savedRules().length === 0;
    }

    submit() {
        this.submitted = true;
        if (this.form.invalid || this.showRulesError() || this.saving) {
            this.form.markAllAsTouched();
            return;
        }
        this.saving = true;
        const value = this.form.value;
        const email = this.actual ? this.actual.email : value.email;
        const userDTO: UserDTO = {
            id: this.actual?.id,
            activated: value.activated,
            email,
            login: email,
            firstName: value.firstname,
            lastName: value.lastname,
            langKey: value.lang,
            mobile: value.phone,
            admin: value.admin,
            mfaEmail: value.mfaEmail,
            mfaSms: value.mfaSms,
            rules: this.savedRules()
        };
        const request: Observable<unknown> = this.actual
            ? this.gatewayPublicUserService.updateUser(userDTO)
            : this.gatewayPublicUserService.createUser(userDTO);
        request.subscribe({
            next: () => {
                const navigationExtras: NavigationExtras = { state: { message: this.actual ? 'success_updated' : 'success_created' } };
                this.router.navigate([`/h/admin/user-management`], navigationExtras);
            },
            error: (e) => {
                this.saving = false;
                // A server error with a title (email already used…) has already been shown by ErrorsInterceptor,
                // which then rethrows a string: only the errors it let through get a toast here.
                if (e instanceof HttpErrorResponse) {
                    this.messageService.add({
                        key: 'common', severity: 'error', summary: this.translate.instant('userEdit.toasts.errorSummary'),
                        detail: this.translate.instant(this.actual ? 'userEdit.toasts.saveErrorDetail' : 'userEdit.toasts.createErrorDetail')
                    });
                }
            }
        });
    }

    openRuleDialog(index: number | null = null) {
        const rule = index === null ? null : this.userRules[index];
        const tenant = rule ? rule.tenant : this.ruleTenants[0]?.code ?? null;
        this.editedRuleIndex = index;
        this.ruleFormPartners = this.grantablePartners(tenant);
        this.ruleForm.reset({
            tenant,
            partner: rule ? rule.partner : this.ruleFormPartners[0]?.code ?? NONE,
            type: rule && rule.rule === NONE ? 'role' : 'rule',
            rule: rule && rule.rule !== NONE ? rule.rule : null,
            role: rule && rule.role !== NONE ? rule.role : null
        });
        this.ruleFormRoles = this.grantableRoles();
        this.ruleDialogVisible = true;
    }

    // The partners depend on the group: a partner of the previous group no longer applies.
    onRuleTenantChange() {
        this.ruleFormPartners = this.grantablePartners(this.ruleForm.value.tenant);
        const partner = this.ruleForm.get('partner');
        if (!this.ruleFormPartners.some(p => p.code === partner.value)) {
            partner.setValue(this.ruleFormPartners[0]?.code ?? NONE);
        }
        this.onRulePartnerChange();
    }

    // The roles depend on the partner: an admin tenant grants ROLE_ADMIN only with a given partner.
    onRulePartnerChange() {
        this.ruleFormRoles = this.grantableRoles();
        const role = this.ruleForm.get('role');
        if (role.value && !this.ruleFormRoles.some(r => r.code === role.value)) {
            role.setValue(null);
        }
    }

    showRightError(): boolean {
        const control = this.ruleForm.get(this.ruleForm.value.type);
        return this.ruleForm.hasError('rightRequired') && (control.dirty || control.touched);
    }

    applyRule() {
        if (this.ruleForm.invalid) {
            this.ruleForm.markAllAsTouched();
            return;
        }
        const { tenant, partner, type, rule, role } = this.ruleForm.value;
        const value: RuleDmpDto = {
            tenant,
            partner,
            rule: type === 'rule' ? rule : NONE,
            role: type === 'role' ? role : NONE
        };
        this.userRules = this.editedRuleIndex === null
            ? [...this.userRules, value]
            : this.userRules.map((el, i) => i === this.editedRuleIndex ? value : el);
        this.ruleDialogVisible = false;
    }

    removeRule(index: number) {
        this.userRules = this.userRules.filter((el, i) => i !== index);
    }

    partnerOptions(tenant: string): Select[] {
        const partners = this.datastores.find(d => d.code === tenant)?.partners ?? [];
        return [...this.partnerChoices, ...partners.map(p => ({ code: p.code, name: p.name }))];
    }

    // Partners on which the admin may grant a right other than ROLE_ADMIN: all of them for an admin tenant of the group,
    // his own for an admin affilié.
    private grantablePartners(tenant: string): Select[] {
        return this.partnerOptions(tenant).filter(p => this.scope.covers({ tenant, partner: p.code, role: NONE, rule: NONE }));
    }

    private grantableRoles(): Select[] {
        const { tenant, partner } = this.ruleForm.value;
        return this.roles.filter(r => this.scope.covers({ tenant, partner, role: r.code, rule: NONE }));
    }

    // Rules without right (neither rule nor role) are not saved.
    private savedRules(): RuleDmpDto[] {
        return this.userRules.filter((el) => !(el.rule === NONE && el.role === NONE));
    }

    groupName(code: string): string {
        return this.datastores.find(d => d.code === code)?.name ?? code;
    }

    partnerName(rule: RuleDmpDto): string {
        return this.partnerOptions(rule.tenant).find(p => p.code === rule.partner)?.name ?? rule.partner;
    }
}
